import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

interface HousingBurdenSectionProps {
  location: string;
  geofips?: string | null;
  embedOnly?: boolean;
}

interface HousingBurdenData {
  year: number;
  renter_35plus?: number;
  owner_35plus?: number;
  renter_all?: Record<string, number>;
  owner_all?: Record<string, number>;
  renter_percentile?: number;
  owner_percentile?: number;
}

interface RenterHousingBurdenRow {
  year: number;
  housing_burden: string;
  count: number;
  percentile: number | null;
}

interface OwnerHousingBurdenRow {
  year: number;
  owner_housing_burden_level: string;
  mortgage_status: string;
  percent: number;
  percentile: number | null;
}

interface HousingBurdenMetrics {
  latestYear: number | null;
  renter35Plus: number | null;
  owner35Plus: number | null;
  renterPercentile: number | null;
  ownerPercentile: number | null;
}

const HousingBurdenSection: React.FC<HousingBurdenSectionProps> = ({ location, geofips, embedOnly = false }) => {
  const [activeTab, setActiveTab] = useState<'35plus' | 'renter' | 'owner'>('35plus');
  const [activeView, setActiveView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');
  const [chartData, setChartData] = useState<any>(null);
  const [metrics, setMetrics] = useState<HousingBurdenMetrics>({
    latestYear: null,
    renter35Plus: null,
    owner35Plus: null,
    renterPercentile: null,
    ownerPercentile: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get geofips if not provided
        let locationGeofips: string | null = geofips ? String(geofips) : null;
        
        if (!locationGeofips) {
          const geofipsNum = await supabaseLocationService.getGeofips(location);
          locationGeofips = geofipsNum ? String(geofipsNum) : null;
        }

        if (!locationGeofips) {
          throw new Error(`Could not find geofips for location: ${location}`);
        }

        // Fetch renter data from housing_burden_renter table
        const renterResult = await supabaseClient
          .from('housing_burden_renter')
          .select('year, housing_burden, count, percentile')
          .ilike('location_name', `%${location}%`)
          .order('year', { ascending: true });

        const renterData: RenterHousingBurdenRow[] = renterResult.data || [];
        const renterError = renterResult.error;

        // Log raw data for debugging - especially for San Luis Obispo
        if (location.toLowerCase().includes('san luis obispo') || location.toLowerCase().includes('slo')) {
          console.log(`🔍 HousingBurdenSection - Raw renter data for ${location}:`, renterData);
          console.log(`🔍 HousingBurdenSection - Renter data by year:`, 
            renterData.reduce((acc: any, row: RenterHousingBurdenRow) => {
              if (!acc[row.year]) acc[row.year] = [];
              acc[row.year].push({
                housing_burden: row.housing_burden,
                count: row.count,
                percentile: row.percentile
              });
              return acc;
            }, {})
          );
        }

        if (renterError || renterData.length === 0) {
          console.error('Renter data error details:', renterError);
          throw new Error(`Failed to fetch renter data: ${renterError?.message || 'No data found'}`);
        }

        // Fetch owner data from owner_housing_burden table
        const ownerResult = await supabaseClient
          .from('owner_housing_burden')
          .select('year, owner_housing_burden_level, mortgage_status, percent, percentile')
          .ilike('location_name', `%${location}%`)
          .order('year', { ascending: true });

        const ownerData: OwnerHousingBurdenRow[] = ownerResult.data || [];
        const ownerError = ownerResult.error;

        if (ownerError || ownerData.length === 0) {
          console.error('Owner data error details:', ownerError);
          throw new Error(`Failed to fetch owner data: ${ownerError?.message || 'No data found'}`);
        }

        // Fetch California average data for renters
        const californiaRenterResult = await supabaseClient
          .from('housing_burden_renter')
          .select('year, housing_burden, count, percentile')
          .ilike('location_name', 'California')
          .order('year', { ascending: true });

        const californiaRenterData: RenterHousingBurdenRow[] = californiaRenterResult.data || [];

        // Fetch California average data for owners
        const californiaOwnerResult = await supabaseClient
          .from('owner_housing_burden')
          .select('year, owner_housing_burden_level, mortgage_status, percent, percentile')
          .ilike('location_name', 'California')
          .order('year', { ascending: true });

        const californiaOwnerData: OwnerHousingBurdenRow[] = californiaOwnerResult.data || [];

        if (!renterData || !ownerData) {
          throw new Error('No data found for this location');
        }

        // Process data by year
        const years = new Set<number>();
        renterData.forEach((r: RenterHousingBurdenRow) => years.add(r.year));
        ownerData.forEach((o: OwnerHousingBurdenRow) => years.add(o.year));
        const sortedYears = Array.from(years).sort((a, b) => a - b);

        // Get latest year for metrics - use the most recent year that has data
        const latestYear = Math.max(...sortedYears);
        
        // Get latest years for renter and owner separately (they may differ)
        const renterYears = new Set<number>();
        renterData.forEach((r: RenterHousingBurdenRow) => renterYears.add(r.year));
        const latestRenterYear = renterYears.size > 0 ? Math.max(...Array.from(renterYears)) : null;
        
        const ownerYears = new Set<number>();
        ownerData.forEach((o: OwnerHousingBurdenRow) => ownerYears.add(o.year));
        const latestOwnerYear = ownerYears.size > 0 ? Math.max(...Array.from(ownerYears)) : null;

        // Process 35%+ data
        const renter35PlusByYear: Record<number, number> = {};
        const owner35PlusByYear: Record<number, number> = {};
        const renterPercentileByYear: Record<number, number> = {};
        const ownerPercentileByYear: Record<number, number> = {};
        
        // California averages
        const californiaRenter35PlusByYear: Record<number, number> = {};
        const californiaOwner35PlusByYear: Record<number, number> = {};

        // Process renter data
        // Handle duplicate rows by grouping by year and burden category
        const renterDataByYearAndBurden: Record<number, Record<string, RenterHousingBurdenRow[]>> = {};
        renterData.forEach((row: RenterHousingBurdenRow) => {
          const year = row.year;
          const burden = row.housing_burden;
          if (!renterDataByYearAndBurden[year]) {
            renterDataByYearAndBurden[year] = {};
          }
          if (!renterDataByYearAndBurden[year][burden]) {
            renterDataByYearAndBurden[year][burden] = [];
          }
          renterDataByYearAndBurden[year][burden].push(row);
        });

        // Process each year
        Object.keys(renterDataByYearAndBurden).forEach(yearStr => {
          const year = Number(yearStr);
          const yearData = renterDataByYearAndBurden[year];
          
          // Get total - use the largest "All" count if there are duplicates
          const allRows = yearData['All'] || [];
          const total = allRows.length > 0 
            ? Math.max(...allRows.map(r => r.count || 0))
            : 0;
          
          // Get 35%+ burdened - use the row with the percentile if available, otherwise the largest count
          const burdened35Rows = yearData['35.0 percent or more'] || [];
          if (burdened35Rows.length > 0) {
            // Prefer row with percentile data, otherwise use largest count
            const burdenedRow = burdened35Rows.find(r => r.percentile !== null && r.percentile !== undefined) 
              || burdened35Rows.reduce((max, r) => (r.count || 0) > (max.count || 0) ? r : max);
            
            const count = burdenedRow.count || 0;
            const percentile = burdenedRow.percentile;
            
            if (total > 0) {
              renter35PlusByYear[year] = (count / total) * 100;
              
              // Log calculation details for San Luis Obispo
              if (location.toLowerCase().includes('san luis obispo') || location.toLowerCase().includes('slo')) {
                console.log(`🔍 HousingBurdenSection - Calculated renter 35%+ for ${year}:`, {
                  burdened_count: count,
                  total_count: total,
                  percentage: renter35PlusByYear[year],
                  percentile: percentile,
                  duplicate_rows_found: burdened35Rows.length,
                  all_rows_found: allRows.length
                });
              }
            }
            if (percentile !== null && percentile !== undefined && percentile > 0) {
              // Convert from 0-1 range to 0-100 range if needed
              renterPercentileByYear[year] = percentile <= 1 ? percentile * 100 : percentile;
            }
          }
        });

        // Process California renter data
        const californiaRenterDataByYearAndBurden: Record<number, Record<string, RenterHousingBurdenRow[]>> = {};
        californiaRenterData.forEach((row: RenterHousingBurdenRow) => {
          const year = row.year;
          const burden = row.housing_burden;
          if (!californiaRenterDataByYearAndBurden[year]) {
            californiaRenterDataByYearAndBurden[year] = {};
          }
          if (!californiaRenterDataByYearAndBurden[year][burden]) {
            californiaRenterDataByYearAndBurden[year][burden] = [];
          }
          californiaRenterDataByYearAndBurden[year][burden].push(row);
        });

        Object.keys(californiaRenterDataByYearAndBurden).forEach(yearStr => {
          const year = Number(yearStr);
          const yearData = californiaRenterDataByYearAndBurden[year];
          
          const allRows = yearData['All'] || [];
          const total = allRows.length > 0 
            ? Math.max(...allRows.map(r => r.count || 0))
            : 0;
          
          const burdened35Rows = yearData['35.0 percent or more'] || [];
          if (burdened35Rows.length > 0 && total > 0) {
            const burdenedRow = burdened35Rows.find(r => r.percentile !== null && r.percentile !== undefined) 
              || burdened35Rows.reduce((max, r) => (r.count || 0) > (max.count || 0) ? r : max);
            californiaRenter35PlusByYear[year] = (burdenedRow.count || 0) / total * 100;
          }
        });

        // Process owner data - owner_housing_burden table uses owner_housing_burden_level and percent
        // Note: owner table has mortgage_status, so we need to aggregate or filter
        // For 35%+, we'll use the row with mortgage_status = 'All' if available, otherwise aggregate
        console.log('🔍 Owner data sample:', ownerData.slice(0, 5));
        
        // First, collect all 35%+ data by year and mortgage status
        const owner35PlusData: Record<number, { 'All'?: { percent: number; percentile: number | null }, 'with mortgage'?: { percent: number; percentile: number | null }, 'without mortgage'?: { percent: number; percentile: number | null } }> = {};
        
        ownerData.forEach((row: OwnerHousingBurdenRow) => {
          const year = row.year;
          const burden = row.owner_housing_burden_level;
          const mortgageStatus = row.mortgage_status;
          const percent = row.percent || 0;
          const percentile = row.percentile;

          if (burden === '35.0 percent or more') {
            if (!owner35PlusData[year]) {
              owner35PlusData[year] = {};
            }
            owner35PlusData[year][mortgageStatus as 'All' | 'with mortgage' | 'without mortgage'] = { percent, percentile };
          }
        });
        
        // Now process the collected data, prioritizing 'All' > 'with mortgage' > 'without mortgage'
        Object.keys(owner35PlusData).forEach(yearStr => {
          const year = parseInt(yearStr);
          const yearData = owner35PlusData[year];
          
          // Priority: All > with mortgage > without mortgage
          if (yearData['All']) {
            owner35PlusByYear[year] = yearData['All'].percent;
            if (yearData['All'].percentile !== null && yearData['All'].percentile !== undefined && yearData['All'].percentile > 0) {
              ownerPercentileByYear[year] = yearData['All'].percentile <= 1 ? yearData['All'].percentile * 100 : yearData['All'].percentile;
            }
          } else if (yearData['with mortgage']) {
            owner35PlusByYear[year] = yearData['with mortgage'].percent;
            if (yearData['with mortgage'].percentile !== null && yearData['with mortgage'].percentile !== undefined && yearData['with mortgage'].percentile > 0) {
              ownerPercentileByYear[year] = yearData['with mortgage'].percentile <= 1 ? yearData['with mortgage'].percentile * 100 : yearData['with mortgage'].percentile;
            }
          } else if (yearData['without mortgage']) {
            owner35PlusByYear[year] = yearData['without mortgage'].percent;
            if (yearData['without mortgage'].percentile !== null && yearData['without mortgage'].percentile !== undefined && yearData['without mortgage'].percentile > 0) {
              ownerPercentileByYear[year] = yearData['without mortgage'].percentile <= 1 ? yearData['without mortgage'].percentile * 100 : yearData['without mortgage'].percentile;
            }
          }
        });

        // Process California owner data
        const californiaOwner35PlusData: Record<number, { 'All'?: { percent: number }, 'with mortgage'?: { percent: number }, 'without mortgage'?: { percent: number } }> = {};
        
        californiaOwnerData.forEach((row: OwnerHousingBurdenRow) => {
          const year = row.year;
          const burden = row.owner_housing_burden_level;
          const mortgageStatus = row.mortgage_status;
          const percent = row.percent || 0;

          if (burden === '35.0 percent or more') {
            if (!californiaOwner35PlusData[year]) {
              californiaOwner35PlusData[year] = {};
            }
            californiaOwner35PlusData[year][mortgageStatus as 'All' | 'with mortgage' | 'without mortgage'] = { percent };
          }
        });
        
        Object.keys(californiaOwner35PlusData).forEach(yearStr => {
          const year = parseInt(yearStr);
          const yearData = californiaOwner35PlusData[year];
          
          if (yearData['All']) {
            californiaOwner35PlusByYear[year] = yearData['All'].percent;
          } else if (yearData['with mortgage']) {
            californiaOwner35PlusByYear[year] = yearData['with mortgage'].percent;
          } else if (yearData['without mortgage']) {
            californiaOwner35PlusByYear[year] = yearData['without mortgage'].percent;
          }
        });
        
        console.log('🔍 Owner 35%+ data by year and mortgage status:', owner35PlusData);
        console.log('🔍 Owner 35%+ by year:', owner35PlusByYear);
        console.log('🔍 Owner percentile by year:', ownerPercentileByYear);
        console.log('🔍 Latest years - Overall:', latestYear, 'Renter:', latestRenterYear, 'Owner:', latestOwnerYear);

        // Set metrics - use latest year for each data type separately
        const finalRenter35Plus = latestRenterYear ? (renter35PlusByYear[latestRenterYear] || null) : null;
        const finalOwner35Plus = latestOwnerYear ? (owner35PlusByYear[latestOwnerYear] || null) : null;
        const finalRenterPercentile = latestRenterYear ? (renterPercentileByYear[latestRenterYear] || null) : null;
        const finalOwnerPercentile = latestOwnerYear ? (ownerPercentileByYear[latestOwnerYear] || null) : null;
        
        console.log('🔍 Final metrics being set:', {
          latestYear,
          latestRenterYear,
          latestOwnerYear,
          renter35Plus: finalRenter35Plus,
          owner35Plus: finalOwner35Plus,
          renterPercentile: finalRenterPercentile,
          ownerPercentile: finalOwnerPercentile,
          'ownerPercentileByYear[2022]': ownerPercentileByYear[2022],
          'ownerPercentileByYear keys': Object.keys(ownerPercentileByYear),
        });
        
        setMetrics({
          latestYear,
          renter35Plus: finalRenter35Plus,
          owner35Plus: finalOwner35Plus,
          renterPercentile: finalRenterPercentile,
          ownerPercentile: finalOwnerPercentile,
        });

        // Prepare chart data based on active tab
        if (activeTab === '35plus') {
          // Chart 1: 35%+ for both renters and owners
          const datasets = [
            {
              label: 'Renters (35%+)',
              data: sortedYears.map(year => renter35PlusByYear[year] || null),
              borderColor: 'rgba(239, 68, 68, 1)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderWidth: 2,
              fill: false,
              tension: 0.1,
            },
            {
              label: 'Owners (35%+)',
              data: sortedYears.map(year => owner35PlusByYear[year] || null),
              borderColor: 'rgba(59, 130, 246, 1)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 2,
              fill: false,
              tension: 0.1,
            },
            {
              label: 'California Renters Avg (35%+)',
              data: sortedYears.map(year => californiaRenter35PlusByYear[year] || null),
              borderColor: 'rgba(239, 68, 68, 0.5)',
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              borderWidth: 2,
              borderDash: [5, 5],
              fill: false,
              tension: 0.1,
            },
            {
              label: 'California Owners Avg (35%+)',
              data: sortedYears.map(year => californiaOwner35PlusByYear[year] || null),
              borderColor: 'rgba(59, 130, 246, 0.5)',
              backgroundColor: 'rgba(59, 130, 246, 0.05)',
              borderWidth: 2,
              borderDash: [5, 5],
              fill: false,
              tension: 0.1,
            },
          ];
          setChartData({ labels: sortedYears.map(y => y.toString()), datasets });
        } else if (activeTab === 'renter') {
          // Chart 2: All classifications for renters
          const burdenCategories = [
            '20.0 to 24.9 percent',
            '25.0 to 29.9 percent',
            '30.0 to 34.9 percent',
            '35.0 percent or more',
          ];
          
          const renterDataByCategory: Record<string, Record<number, number>> = {};
          burdenCategories.forEach(cat => {
            renterDataByCategory[cat] = {};
          });

          // Use the grouped data structure to handle duplicates
          sortedYears.forEach(year => {
            const yearData = renterDataByYearAndBurden[year];
            if (!yearData) return;
            
            // Get total - use the largest "All" count if there are duplicates
            const allRows = yearData['All'] || [];
            const total = allRows.length > 0 
              ? Math.max(...allRows.map(r => r.count || 0))
              : 0;
            
            if (total === 0) return;
            
            burdenCategories.forEach(category => {
              const categoryRows = yearData[category] || [];
              if (categoryRows.length > 0) {
                // Use the row with the largest count (most likely the correct one)
                const categoryRow = categoryRows.reduce((max, r) => 
                  (r.count || 0) > (max.count || 0) ? r : max
                );
                renterDataByCategory[category][year] = (categoryRow.count || 0) / total * 100;
              }
            });
          });

          const datasets = burdenCategories.map((cat, idx) => {
            const colors = [
              'rgba(16, 185, 129, 1)',   // Green
              'rgba(245, 158, 11, 1)',   // Yellow
              'rgba(251, 146, 60, 1)',   // Orange
              'rgba(239, 68, 68, 1)',    // Red
            ];
            return {
              label: cat,
              data: sortedYears.map(year => renterDataByCategory[cat][year] || null),
              borderColor: colors[idx],
              backgroundColor: colors[idx].replace('1)', '0.1)'),
              borderWidth: 2,
              fill: false,
              tension: 0.1,
            };
          });

          // Add California averages for renter categories
          const californiaRenterDataByCategory: Record<string, Record<number, number>> = {};
          burdenCategories.forEach(cat => {
            californiaRenterDataByCategory[cat] = {};
          });

          sortedYears.forEach(year => {
            const yearData = californiaRenterDataByYearAndBurden[year];
            if (!yearData) return;
            
            const allRows = yearData['All'] || [];
            const total = allRows.length > 0 
              ? Math.max(...allRows.map(r => r.count || 0))
              : 0;
            
            if (total === 0) return;
            
            burdenCategories.forEach(category => {
              const categoryRows = yearData[category] || [];
              if (categoryRows.length > 0) {
                const categoryRow = categoryRows.reduce((max, r) => 
                  (r.count || 0) > (max.count || 0) ? r : max
                );
                californiaRenterDataByCategory[category][year] = (categoryRow.count || 0) / total * 100;
              }
            });
          });

          // Add California average datasets with dashed lines
          burdenCategories.forEach((cat, idx) => {
            const colors = [
              'rgba(16, 185, 129, 0.5)',   // Green
              'rgba(245, 158, 11, 0.5)',   // Yellow
              'rgba(251, 146, 60, 0.5)',   // Orange
              'rgba(239, 68, 68, 0.5)',    // Red
            ];
            datasets.push({
              label: `CA Avg: ${cat}`,
              data: sortedYears.map(year => californiaRenterDataByCategory[cat][year] || null),
              borderColor: colors[idx],
              backgroundColor: colors[idx].replace('0.5)', '0.05)'),
              borderWidth: 2,
              borderDash: [5, 5],
              fill: false,
              tension: 0.1,
            } as any);
          });

          setChartData({ labels: sortedYears.map(y => y.toString()), datasets });
        } else if (activeTab === 'owner') {
          // Chart 3: All classifications for owners
          const burdenCategories = [
            '20.0 to 24.9 percent',
            '25.0 to 29.9 percent',
            '30.0 to 34.9 percent',
            '35.0 percent or more',
          ];
          
          const ownerDataByCategory: Record<string, Record<number, number>> = {};
          burdenCategories.forEach(cat => {
            ownerDataByCategory[cat] = {};
          });

          ownerData.forEach((row: OwnerHousingBurdenRow) => {
            const year = row.year;
            const burden = row.owner_housing_burden_level;
            const percent = row.percent || 0; // owner table uses 'percent' column
            
            if (burdenCategories.includes(burden)) {
              // For owner table, percent is already a percentage value
              ownerDataByCategory[burden][year] = percent;
            }
          });

          const datasets = burdenCategories.map((cat, idx) => {
            const colors = [
              'rgba(16, 185, 129, 1)',   // Green
              'rgba(245, 158, 11, 1)',   // Yellow
              'rgba(251, 146, 60, 1)',   // Orange
              'rgba(239, 68, 68, 1)',    // Red
            ];
            return {
              label: cat,
              data: sortedYears.map(year => ownerDataByCategory[cat][year] || null),
              borderColor: colors[idx],
              backgroundColor: colors[idx].replace('1)', '0.1)'),
              borderWidth: 2,
              fill: false,
              tension: 0.1,
            };
          });

          // Add California averages for owner categories
          const californiaOwnerDataByCategory: Record<string, Record<number, number>> = {};
          burdenCategories.forEach(cat => {
            californiaOwnerDataByCategory[cat] = {};
          });

          californiaOwnerData.forEach((row: OwnerHousingBurdenRow) => {
            const year = row.year;
            const burden = row.owner_housing_burden_level;
            const percent = row.percent || 0;
            
            if (burdenCategories.includes(burden)) {
              californiaOwnerDataByCategory[burden][year] = percent;
            }
          });

          // Add California average datasets with dashed lines
          burdenCategories.forEach((cat, idx) => {
            const colors = [
              'rgba(16, 185, 129, 0.5)',   // Green
              'rgba(245, 158, 11, 0.5)',   // Yellow
              'rgba(251, 146, 60, 0.5)',   // Orange
              'rgba(239, 68, 68, 0.5)',    // Red
            ];
            datasets.push({
              label: `CA Avg: ${cat}`,
              data: sortedYears.map(year => californiaOwnerDataByCategory[cat][year] || null),
              borderColor: colors[idx],
              backgroundColor: colors[idx].replace('0.5)', '0.05)'),
              borderWidth: 2,
              borderDash: [5, 5],
              fill: false,
              tension: 0.1,
            } as any);
          });

          setChartData({ labels: sortedYears.map(y => y.toString()), datasets });
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching housing burden data:', err);
        setError(err.message || 'Failed to load housing burden data');
        setLoading(false);
      }
    };

    fetchData();
  }, [location, geofips, activeTab]);

  // Create chart
  useEffect(() => {
    if (!chartRef.current || !chartData) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const chartConfig = {
      type: 'line' as const,
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top' as const,
            labels: {
              usePointStyle: true,
              padding: 15,
            },
          },
          tooltip: {
            mode: 'index' as const,
            intersect: false,
            callbacks: {
              label: (context: any) => {
                const value = context.parsed.y;
                if (value === null || value === undefined) return '';
                return `${context.dataset.label}: ${value.toFixed(1)}%`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            title: {
              display: true,
              text: 'Percentage of Households (%)',
            },
            ticks: {
              callback: (value: any) => `${value}%`,
            },
          },
          x: {
            title: {
              display: true,
              text: 'Year',
            },
          },
        },
        interaction: {
          mode: 'nearest' as const,
          axis: 'x' as const,
          intersect: false,
        },
      },
    };

    chartInstance.current = new Chart(ctx, chartConfig);

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData]);

  if (loading) {
    return (
      <ChartLayout
        title="Housing Burden"
        chartType="line"
        chartContent={
          <div className="flex items-center justify-center h-96">
            <div className="text-gray-600">Loading housing burden data...</div>
          </div>
        }
        activeView={activeView}
        onViewChange={(view) => setActiveView(view as typeof activeView)}
      />
    );
  }

  if (error) {
    return (
      <ChartLayout
        title="Housing Burden"
        chartType="line"
        chartContent={
          <div className="flex items-center justify-center h-96">
            <div className="text-red-600">Error: {error}</div>
          </div>
        }
        activeView={activeView}
        onViewChange={(view) => setActiveView(view as typeof activeView)}
      />
    );
  }

  const chartContent = (
    <div style={{ height: embedOnly ? '380px' : '384px', width: '100%' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  if (embedOnly) {
    return chartContent;
  }

  return (
    <ChartLayout
      title="Housing Burden"
      chartType="line"
      keyMetrics={
        <KeyMetrics>
          {activeTab === '35plus' && (
            <>
              <KeyMetric
                value={metrics.renter35Plus !== null ? `${metrics.renter35Plus.toFixed(1)}%` : 'N/A'}
                label={`Renters 35%+ (${metrics.latestYear || 'N/A'})`}
                color="#ef4444"
              />
              <KeyMetric
                value={metrics.owner35Plus !== null ? `${metrics.owner35Plus.toFixed(1)}%` : 'N/A'}
                label={`Owners 35%+ (${metrics.latestYear || 'N/A'})`}
                color="#3b82f6"
              />
              <KeyMetric
                value={metrics.renterPercentile !== null ? `${metrics.renterPercentile.toFixed(0)}th` : 'N/A'}
                label="Renter Percentile"
                color="#f59e0b"
              />
              <KeyMetric
                value={metrics.ownerPercentile !== null ? `${metrics.ownerPercentile.toFixed(0)}th` : 'N/A'}
                label="Owner Percentile"
                color="#10b981"
              />
            </>
          )}
          {(activeTab === 'renter' || activeTab === 'owner') && (
            <>
              <KeyMetric
                value={metrics.latestYear ? metrics.latestYear.toString() : 'N/A'}
                label="Latest Year"
                color="#1e3a8a"
              />
              {activeTab === 'renter' && (
                <KeyMetric
                  value={metrics.renterPercentile !== null ? `${metrics.renterPercentile.toFixed(0)}th` : 'N/A'}
                  label="Renter Percentile (35%+)"
                  color="#ef4444"
                />
              )}
              {activeTab === 'owner' && (
                <KeyMetric
                  value={metrics.ownerPercentile !== null ? `${metrics.ownerPercentile.toFixed(0)}th` : 'N/A'}
                  label="Owner Percentile (35%+)"
                  color="#3b82f6"
                />
              )}
            </>
          )}
        </KeyMetrics>
      }
      chartSelector={{
        value: activeTab,
        onChange: (value: string) => setActiveTab(value as '35plus' | 'renter' | 'owner'),
        options: [
          { value: '35plus', label: '35% or Higher' },
          { value: 'renter', label: 'Renters (All)' },
          { value: 'owner', label: 'Owners (All)' },
        ],
      }}
      chartContent={chartContent}
      overviewContent={
        <OverviewContent
          title="Housing Burden Overview"
          description="Housing burden measures the percentage of household income spent on housing costs, including rent or mortgage payments, utilities, and other housing-related expenses."
        >
          <p>
            Housing burden is a critical indicator of housing affordability and economic stability. 
            Households spending 30% or more of their income on housing are considered cost-burdened, 
            while those spending 50% or more are severely cost-burdened. This analysis examines housing 
            burden across different income thresholds (20-24.9%, 25-29.9%, 30-34.9%, and 35%+) for both 
            renters and homeowners, providing insights into housing affordability trends over time.
          </p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Climate Action"
          description="Housing burden directly impacts a community's ability to implement climate solutions, affecting energy efficiency investments, transportation choices, and equitable access to clean energy technologies."
        >
          <div className="methodology-details-simple">
            <h5>Energy Efficiency and Electrification Barriers</h5>
            <p>
              Cost-burdened households have limited disposable income to invest in energy-efficient 
              appliances, home improvements, heat pumps, or rooftop solar systems. High housing costs 
              reduce the financial capacity for upfront investments in climate solutions, even when 
              long-term savings are significant. This creates a barrier to achieving building 
              electrification and energy efficiency goals outlined in climate action plans.
            </p>
            <h5>Transportation Emissions and Location Choice</h5>
            <p>
              High housing costs in urban centers can push residents to more affordable but 
              car-dependent suburban or exurban areas, increasing vehicle miles traveled (VMT) and 
              transportation emissions. This undermines compact development goals and transit-oriented 
              development strategies that are central to reducing transportation sector emissions. 
              Addressing housing affordability in transit-accessible areas is essential for achieving 
              VMT reduction targets.
            </p>
            <h5>Electric Vehicle Adoption</h5>
            <p>
              Housing burden limits access to electric vehicles, which often require home charging 
              infrastructure. Renters and cost-burdened homeowners may lack access to charging 
              infrastructure or the financial capacity for EV purchases, even with incentives. 
              This creates equity gaps in transportation electrification and limits progress toward 
              zero-emission vehicle goals.
            </p>
            <h5>Resilience and Adaptation</h5>
            <p>
              Cost-burdened households have fewer resources to invest in climate resilience measures 
              such as home weatherization, backup power systems, or flood protection. This increases 
              vulnerability to climate impacts like extreme heat, power outages, and flooding, 
              particularly in communities already facing environmental justice concerns.
            </p>
            <h5>Policy Design and Implementation</h5>
            <p>
              Understanding housing burden helps ensure climate policies don't create additional 
              financial burdens for low-income households. Programs must include targeted financial 
              assistance, income-based incentives, and flexible financing options to ensure all 
              residents can participate in climate action initiatives, regardless of housing cost burden.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations"
          description="Housing burden creates significant equity challenges in climate action, disproportionately affecting low-income households, communities of color, and renters who face barriers to accessing climate solutions."
        >
          <div className="methodology-details-simple">
            <h5>Income-Based Disparities</h5>
            <p>
              Lower-income households face significantly higher housing burden rates, with many 
              spending 50% or more of their income on housing costs. This leaves little disposable 
              income for energy efficiency upgrades, electric vehicles, or other climate solutions 
              that require upfront investments. Even with rebates and incentives, the remaining 
              costs can be prohibitive for cost-burdened households, creating a participation gap 
              in climate programs that widens existing economic inequalities.
            </p>
            <h5>Racial and Ethnic Disparities</h5>
            <p>
              Communities of color often experience higher housing burden rates due to historical 
              discrimination in housing, lending, and employment, resulting in wealth gaps and 
              income inequality. These disparities create compounding barriers to participating in 
              climate action initiatives, as communities already facing environmental justice 
              concerns have fewer resources to invest in climate solutions. This perpetuates 
              inequitable distribution of both climate benefits and climate burdens.
            </p>
            <h5>Renter vs. Owner Disparities</h5>
            <p>
              Renters face unique challenges, as they typically cannot make energy efficiency 
              improvements or install rooftop solar without landlord approval. Renters also have 
              less control over their housing costs and may face displacement when landlords 
              upgrade properties. The split incentive problem—where landlords pay for improvements 
              but tenants benefit from lower utility costs—creates additional barriers to building 
              electrification and energy efficiency in rental housing, which represents a significant 
              portion of the housing stock in many communities.
            </p>
            <h5>Geographic and Neighborhood Effects</h5>
            <p>
              High housing burden often correlates with neighborhoods that have limited access to 
              public transit, fewer green spaces, and greater exposure to environmental hazards. 
              These communities may be pushed to car-dependent areas due to housing costs, further 
              increasing transportation emissions and reducing access to climate-friendly 
              infrastructure. Addressing housing burden in transit-accessible, walkable 
              neighborhoods is essential for equitable climate action.
            </p>
            <h5>Intergenerational and Long-Term Impacts</h5>
            <p>
              High housing burden can limit wealth accumulation and intergenerational mobility, 
              creating long-term barriers to climate solution adoption. Families spending excessive 
              portions of income on housing have less ability to build savings, invest in home 
              improvements, or pass wealth to future generations. This creates persistent equity 
              gaps that extend beyond individual households to affect entire communities over time.
            </p>
            <h5>Policy Design for Equity</h5>
            <p>
              Climate action policies must be designed with housing burden in mind, including 
              income-based incentives, flexible financing options, and programs specifically 
              targeting cost-burdened households. This includes renters' rights protections, 
              landlord-tenant partnerships for energy upgrades, community solar programs for 
              non-homeowners, and transportation programs that don't require vehicle ownership. 
              Without these targeted approaches, climate policies risk exacerbating existing 
              economic and racial inequities while failing to achieve equitable emissions reductions.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Sources and Methodology"
          description="Housing burden data comes from the American Community Survey (ACS) 5-year estimates."
          dataSources={[
            'U.S. Census Bureau American Community Survey (ACS) 5-year estimates',
            'Housing burden data from housing_burden_renter and housing_burden_owner tables in Supabase'
          ]}
          methodology={[
            'Housing burden is calculated as the percentage of household income spent on gross rent (for renters) or selected monthly owner costs (for owners), including mortgage payments, property taxes, insurance, and utilities.',
            'Data is aggregated by year and housing burden category (20-24.9%, 25-29.9%, 30-34.9%, 35%+).',
            'Percentile rankings compare the location\'s housing burden rate to all California cities and counties, where the 50th percentile represents the median value. Higher percentiles indicate higher housing burden relative to other locations in the state.'
          ]}
        />
      }
      activeView={activeView}
      onViewChange={(view) => setActiveView(view as typeof activeView)}
    />
  );
};

export default HousingBurdenSection;

